import React from 'react';
import styled from '@emotion/styled';
import { css, Theme } from '@emotion/react';
import { Grid, GridItem } from '../../helpers';
import Spacer from '../../helpers/Spacer';
import TeaserCard from '../../components/TeaserCard/TeaserCard';
import Newsletter from '../../components/Newsletter';
import { ModuleHeader, Link } from '../../primitives';
import { colors, mixins } from '../../core/styles/theme/default';
import { rem } from 'polished';

const StyledTeaserNews = styled.aside`
  display: block;
  position: relative;
  padding: ${rem(48)} 0;
`;

const StyledMoreLink = styled(Link)`
  color: ${colors.blue900};
  z-index: 1;
  position: relative;

  .m-richtext & {
    text-decoration: none;
  }

  ${({ theme }) => theme?.stylesExtension?.teaserNews?.moreLink}
`;

const StyledBackground = styled.div(
  ({ hasNewsletter, theme }: { hasNewsletter: boolean; theme?: Theme }) => css`
    width: 100vw;
    height: 100%;
    position: absolute;
    top: 0;
    overflow: hidden;
    opacity: 0.8;
    background: ${colors.blue100};

    ${hasNewsletter &&
    css`
      height: calc(100% + ${rem(128)});
    `}

    ${mixins.responsiveStyles({
      left: {
        zero: '-16px',
        medium: '-24px',
        large: '-48px',
        wide: `calc(-1 * ((100vw - 1088px ) / 2))`,
        ultra: `calc(-1 * ((100vw - 1260px ) / 2))`,
      },
    })}

    figure, .gatsby-image-wrapper {
      height: 100%;
    }

    ${theme?.stylesExtension?.teaserNews?.background}
  `,
);

const StyledGridItem = styled(GridItem)`
  ${mixins.responsiveStyles({
    'margin-bottom': {
      zero: `${rem(16)}`,
      medium: `${rem(24)}`,
      ultra: `${rem(32)}`,
    },
  })}
`;

const TeaserNews = (teaserNewsProps: TeaserNewsProps) => {
  if (teaserNewsProps === null) return null;

  const { title, items, newsletterHref, newsHref, labels } = teaserNewsProps;
  const hasNewsletter = !!(newsletterHref && labels.newsletterText);

  return (
    <>
      <StyledTeaserNews data-anim="orange">
        <StyledBackground hasNewsletter={hasNewsletter} />
        <ModuleHeader title={title || ''} />
        <Spacer space="deci" />

        <Grid columns={{ zero: 12, large: 12 }}>
          {items?.map((data: any, j: number) => (
            <StyledGridItem
              size={{ zero: 12, small: 12, medium: 6, wide: 3 }}
              push={{ zero: 0, ultra: 0 }}
              key={j}
            >
              <TeaserCard data={data} variant="news" />
            </StyledGridItem>
          ))}
        </Grid>
        {labels.showMore ? (
          <>
            <Spacer space="beta" />
            <StyledMoreLink
              linkText={labels.showMore}
              link={{
                href: newsHref,
                title: 'News',
              }}
              iconAfter="AnimatedIconArrowRight"
              className="animate"
            />
          </>
        ) : null}
      </StyledTeaserNews>
      {hasNewsletter ? (
        <>
          <Spacer space="deci" />
          <Newsletter
            newsletterHref={newsletterHref}
            header={labels.newsletterText ?? ''}
            button={labels.newsletterButton}
          />
        </>
      ) : null}
    </>
  );
};

export type TeaserNewsProps = {
  items: any;
  title?: string;
  newsletterHref?: string;
  newsHref: string;
  labels: {
    showMore: string;
    newsletterText?: string;
    newsletterButton?: string;
  };
} | null;

export default TeaserNews;
